import * as React from 'react';
import { useContext } from 'react';

import type { UseTreatmentOn, Treatments } from '../types/Treatment';
import type { FeatureFlag } from '../constants/feature-flags';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const defaultUseTreatmentOn: UseTreatmentOn = (splitName: string) => {
  return false;
};

const defaultSetTreatmentOverrides = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  treatmentOverrides: Treatments,
) => {};

const defaultValue = {
  useTreatmentOn: defaultUseTreatmentOn,
  setTreatmentOverrides: defaultSetTreatmentOverrides,
} as const;

type ContextValue = {
  setTreatmentOverrides: (arg1: Treatments) => void;
  useTreatmentOn: UseTreatmentOn;
};

const Context = React.createContext<ContextValue>(defaultValue);
const { Provider } = Context;

type SplitTreatmentProviderProps = {
  children: React.ReactNode;
  treatmentOverrides?: {
    [key in FeatureFlag]?: boolean;
  };
  useTreatmentOn: (arg1: FeatureFlag) => boolean;
};

const SplitTreatmentProvider = ({
  children,
  treatmentOverrides = {},
  useTreatmentOn,
}: SplitTreatmentProviderProps) => {
  const [treatments, setTreatments] = React.useState(treatmentOverrides);

  // If the treatment exists in the overrides, use it,
  // else we call useTreatmentOn provided from host application (EUA or AC), or defaultUseTreatmentOn
  // treatmentOverrides is used to provide split treatment values fetched from the BFF
  // On the Submission Page, there is no host application, so we only have access to splits returned from BFF
  const useTreatementOnWithOverrides = (splitName: FeatureFlag) => {
    const treatmentValue = useTreatmentOn(splitName);

    if (splitName in treatments) {
      return treatments[splitName];
    }

    return treatmentValue;
  };

  return (
    <Provider
      value={{
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
        useTreatmentOn: useTreatementOnWithOverrides,
        setTreatmentOverrides: setTreatments,
      }}
    >
      {children}
    </Provider>
  );
};

const useSplitTreatment = (splitName: string) => {
  const { useTreatmentOn } = useContext(Context);
  return useTreatmentOn(splitName);
};

const useSetTreatmentOverrides = () => {
  const { setTreatmentOverrides } = useContext(Context);
  return setTreatmentOverrides;
};

export {
  SplitTreatmentProvider,
  defaultUseTreatmentOn,
  useSplitTreatment,
  useSetTreatmentOverrides,
};
